import React, { useState } from 'react';
import {
  Upload,
  UploadFileInfo,
  UploadFileRestrictions,
  UploadFileStatus,
  UploadOnAddEvent
} from '@progress/kendo-react-upload';
import { loadMessages, LocalizationProvider } from '@progress/kendo-react-intl';
import { getService } from '@Shared.Angular/flowingly.hooks/useService';
import { Guid } from '@Shared.Angular/@types/guid';

export type KendoUploadOnAddEvent = UploadOnAddEvent;
export type KendoUploadFileInfo = UploadFileInfo;
export type KendoUploadFileRestrictions = UploadFileRestrictions;

export type KendoReactFileUploadProps = {
  files?: IKendoUploadFile[];
  onFilesAdded: (
    files: File[],
    event: UploadOnAddEvent
  ) => Promise<void> | void;
  acceptedFileExtensions?: string[]; // e.g., ['.png', '.jpg']
  disabled?: boolean;
  multiple?: boolean;
  maxFileSize?: number;
  selectButtonText?: string;
  dropAreaText?: string;
  showFileList?: boolean;
  dataTestId?: string;
};

const buildUploadFile = (file: IKendoUploadFile): UploadFileInfo => {
  return {
    uid: file.id,
    name: file.filename,
    size:
      typeof file.sizeInKiloBytes === 'number'
        ? file.sizeInKiloBytes * 1024
        : undefined,
    status: UploadFileStatus.Uploaded,
    progress: 100
  };
};

export const KendoReactFileUpload: React.FC<KendoReactFileUploadProps> = ({
  files = [],
  onFilesAdded,
  acceptedFileExtensions,
  disabled = false,
  multiple = false,
  maxFileSize,
  selectButtonText = 'Upload Logo',
  dropAreaText = 'Drag and Drop your files here or click the Upload button',
  showFileList = false,
  dataTestId
}) => {
  const [, setDragDepth] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const uploadFiles = files.map((file) => buildUploadFile(file));

  const restrictions: UploadFileRestrictions = {
    allowedExtensions: acceptedFileExtensions,
    maxFileSize
  };

  const handleAdd = (event: UploadOnAddEvent) => {
    const { affectedFiles } = event;
    if (!affectedFiles.length) {
      return;
    }

    const rawFiles = affectedFiles
      .map((file) => file.getRawFile?.())
      .filter(Boolean);

    const validFiles: File[] = [];
    if (acceptedFileExtensions) {
      for (const file of rawFiles) {
        const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
        if (!acceptedFileExtensions.includes(fileExtension)) {
          getService('notificationService').showWarningToast(
            `${file.name} is not an allowed type (${acceptedFileExtensions.join(
              ', '
            )})`
          );
          // Clear the invalid file from Kendo's internal state
          event.affectedFiles.forEach(
            (f) => (f.validationErrors = ['Invalid file type'])
          );
        } else {
          validFiles.push(file);
        }
      }
    }

    onFilesAdded(validFiles, event);
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    setDragDepth((prev) => {
      const newDepth = prev + 1;
      if (newDepth === 1) {
        setIsDragging(true);
      }
      return newDepth;
    });
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragDepth((prev) => {
      const newDepth = prev - 1;
      if (newDepth === 0) {
        setIsDragging(false);
      }
      return newDepth;
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    setDragDepth(0);
  };

  loadMessages(
    {
      'upload.select': selectButtonText,
      'upload.dropFilesHere': dropAreaText
    },
    'en'
  );

  return (
    <LocalizationProvider language="en">
      <div
        className={`kendo-react-file-upload ${isDragging ? 'dragging' : ''} ${
          files.length > 0 ? 'has-file' : ''
        }`}
        data-testid={dataTestId}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {files.length > 0 && (
          <div className="uploaded-file-display">
            <p className="uploaded-file-name">{files[0].filename}</p>
          </div>
        )}
        <Upload
          autoUpload={false}
          multiple={multiple}
          disabled={disabled}
          showActionButtons={false}
          showFileList={showFileList}
          files={uploadFiles}
          restrictions={restrictions}
          onAdd={handleAdd}
        />
      </div>
    </LocalizationProvider>
  );
};

export interface IKendoUploadFile {
  id: Guid;
  filename: string;
  sizeInKiloBytes?: number;
}
